import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import * as yup from 'yup';
import PasswordTextField from 'components/common/PasswordTextField';
import TurnstileWidget, { TurnstileWidgetHandle } from 'components/common/TurnstileWidget';
import { translateApiMessage } from 'utils/translateApiMessage';
import { applyApiDetailsToForm } from 'utils/apiDetailsToFormErrors';
import DefaultCredentialAlert from '../common/DefaultCredentialAlert';
import SocialAuth from './SocialAuth';

interface LoginFormProps {
  provider?: 'jwt' | 'firebase';
  handleLogin: (data: LoginFormValues) => any;
  signUpLink: string;
  socialAuth?: boolean;
  forgotPasswordLink?: string;
  rememberDevice?: boolean;
  defaultCredential?: { email: string; password: string };
  // Opt-in Turnstile. Off by default so the Aurora-template demo Login page
  // is unaffected; CoupleLogin enables it to match the BE `/api/couple/login`
  // requireTurnstile() middleware.
  enableTurnstile?: boolean;
}
export interface LoginFormValues {
  email: string;
  password: string;
  turnstileToken?: string;
}

const LoginForm = ({
  handleLogin,
  signUpLink,
  forgotPasswordLink,
  socialAuth = true,
  rememberDevice = true,
  defaultCredential,
  enableTurnstile = false,
}: LoginFormProps) => {
  const { t } = useTranslation();
  const turnstileSiteKey = enableTurnstile
    ? (import.meta.env.VITE_TURNSTILE_SITE_KEY || '').trim()
    : '';
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);

  const schema = useMemo(
    () =>
      yup
        .object({
          email: yup
            .string()
            .email(t('validation.email_invalid'))
            .required(t('validation.required')),
          password: yup.string().required(t('validation.required')),
        })
        .required(),
    [t],
  );

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: LoginFormValues) => {
    try {
      let turnstileToken: string | undefined;
      if (turnstileSiteKey && turnstileRef.current) {
        turnstileToken = await turnstileRef.current.execute();
      }
      const payload: LoginFormValues = {
        ...data,
        ...(turnstileToken ? { turnstileToken } : {}),
      };
      await handleLogin(payload);
    } catch (error: unknown) {
      if (!error) return;
      const handled = applyApiDetailsToForm<LoginFormValues>(error, setError, t);
      if (!handled) {
        setError('root.credential', {
          type: 'manual',
          message: translateApiMessage(error, t, { fallback: t('auth.login.error_default') }),
        });
      }
    }
  };

  return (
    <Stack
      direction="column"
      sx={{
        minHeight: 1,
        alignItems: 'center',
        justifyContent: { xs: 'flex-start', lg: 'center' },
        pt: { xs: 4, lg: 10 },
        pb: 10,
      }}
    >
      <Grid
        container
        sx={{
          maxWidth: '35rem',
          rowGap: 4,
          p: { xs: 3, sm: 5 },
          mb: 5,
        }}
      >
        <Grid size={12}>
          <Stack
            direction="column"
            spacing={1}
            sx={{
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
            }}
          >
            <Typography variant="h4">{t('auth.login.title')}</Typography>
            <Typography
              variant="subtitle1"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('auth.login.subtitle')}
            </Typography>
          </Stack>
        </Grid>
        {socialAuth && (
          <>
            <Grid size={12}>
              <SocialAuth />
            </Grid>
            <Grid size={12}>
              <Divider sx={{ color: 'text.secondary' }}>{t('auth.divider.or_use_email')}</Divider>
            </Grid>
          </>
        )}

        <Grid size={12}>
          <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
            {errors.root?.credential?.message && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {errors.root?.credential?.message}
              </Alert>
            )}
            {defaultCredential && <DefaultCredentialAlert />}
            <Grid container>
              <Grid
                sx={{
                  mb: 3,
                }}
                size={12}
              >
                <TextField
                  fullWidth
                  size="large"
                  id="email"
                  type="email"
                  label={t('auth.login.email_label')}
                  defaultValue={defaultCredential?.email}
                  error={!!errors.email}
                  helperText={<>{errors.email?.message}</>}
                  {...register('email')}
                />
              </Grid>
              <Grid
                sx={{
                  mb: 2.5,
                }}
                size={12}
              >
                <PasswordTextField
                  fullWidth
                  size="large"
                  id="password"
                  label={t('auth.login.password_label')}
                  defaultValue={defaultCredential?.password}
                  error={!!errors.password}
                  helperText={<>{errors.password?.message}</>}
                  {...register('password')}
                />
              </Grid>
              <Grid
                sx={{
                  mb: 6,
                }}
                size={12}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    justifyContent: rememberDevice ? 'space-between' : 'flex-end',
                    alignItems: 'center',
                  }}
                >
                  {rememberDevice && (
                    <FormControlLabel
                      control={<Checkbox name="checked" color="primary" size="small" />}
                      label={
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          {t('auth.login.remember_device')}
                        </Typography>
                      }
                    />
                  )}

                  {forgotPasswordLink && (
                    <Link href={forgotPasswordLink} variant="subtitle2" color="text.primary">
                      {t('auth.login.forgot_password_link')}
                    </Link>
                  )}
                </Stack>
              </Grid>
              {turnstileSiteKey && (
                <Grid size={12} sx={{ mb: 3 }}>
                  <Stack direction="column" alignItems="center">
                    <TurnstileWidget ref={turnstileRef} siteKey={turnstileSiteKey} mode="execute" />
                  </Stack>
                </Grid>
              )}
              <Grid size={12}>
                <Button
                  fullWidth
                  type="submit"
                  size="large"
                  variant="contained"
                  color="neutral"
                  loading={isSubmitting}
                  sx={{ py: '14px' }}
                >
                  {t('auth.login.submit')}
                </Button>
              </Grid>
              <Grid size={12} sx={{ mt: 2 }}>
                <Stack direction="row" justifyContent="center">
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                    {t('auth.login.new_user_prefix')}
                    <Link href={signUpLink} sx={{ ml: 1 }}>
                      {t('auth.login.signup_link')}
                    </Link>
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
      {/* <Link href="#!" variant="subtitle2">
        Trouble signing in?
      </Link> */}
    </Stack>
  );
};

export default LoginForm;
